import React, { useEffect, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  PUSH_NOTIFICATION_PREFS,
  EMAIL_NOTIFICATION_PREFS,
  withNotificationDefaults,
  notificationPrefPath,
} from '@coparent/shared/src/utils/notificationPrefs';
import { HiBell, HiMail, HiDeviceMobile, HiOutlineMoon } from 'react-icons/hi';
import { updateQuietHours } from '@coparent/shared/src/services/userPrefsService';
import { normalizeQuietHours, formatQuietWindow } from '@coparent/shared/src/utils/quietHours';
import { DEFAULT_QUIET_HOURS } from '@coparent/shared/src/utils/constants';
import toast from 'react-hot-toast';

// 0-23 as {value,label} for the hour selects.
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour,
  label: `${hour % 12 === 0 ? 12 : hour % 12}:00 ${hour < 12 ? 'AM' : 'PM'}`,
}));

/**
 * Notification preferences for the web app.
 *
 * Web previously had no notification settings at all — the only way to change
 * `notificationPrefs.email` was the unsubscribe link at the bottom of an email,
 * and the push preferences were reachable only from the phone.
 *
 * Two groups, deliberately separated and labelled:
 *
 *   Email  — the only channel web itself delivers. Gates the onboarding email
 *            sequence (functions/onboardingEmails).
 *   Push   — delivered to the coparent app on a phone. The web app has no push
 *            support at all (no service worker, no token), so these toggles
 *            control the user's MOBILE notifications. The heading says so
 *            outright rather than implying browser notifications exist.
 *
 * Preferences are per-user, not per-device, so editing them here is meaningful
 * even for someone who mostly uses their phone — and it's the only way a
 * web-first user can discover the handoff reminder exists.
 */

const Toggle = ({ id, checked, disabled, onChange, label }) => (
  <button
    id={id}
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={[
      'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full',
      'transition-colors duration-200 ease-in-out',
      'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      checked ? 'bg-accent-600' : 'bg-surface-300',
    ].join(' ')}
  >
    <span
      className={[
        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow',
        'transition duration-200 ease-in-out mt-0.5',
        checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5',
      ].join(' ')}
    />
  </button>
);

const PrefRow = ({ pref, checked, saving, onToggle }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-surface-100 last:border-0">
    <div className="min-w-0">
      <label htmlFor={`notif-${pref.key}`} className="font-semibold text-ink-900 text-sm block">
        {pref.label}
      </label>
      <p className="text-xs text-ink-500 mt-0.5">{pref.description}</p>
    </div>
    <Toggle
      id={`notif-${pref.key}`}
      checked={checked}
      disabled={saving}
      onChange={() => onToggle(pref.key)}
      label={pref.label}
    />
  </div>
);

const NotificationSettings = () => {
  const { currentUser, userData } = useAuth();
  const [prefs, setPrefs] = useState(() => withNotificationDefaults(null));
  const [savingKey, setSavingKey] = useState(null);
  const [quiet, setQuiet] = useState(() => normalizeQuietHours(DEFAULT_QUIET_HOURS));
  const [savingQuiet, setSavingQuiet] = useState(false);

  useEffect(() => {
    if (userData?.notificationPrefs) {
      setPrefs(withNotificationDefaults(userData.notificationPrefs));
    }
  }, [userData?.notificationPrefs]);

  useEffect(() => {
    setQuiet(normalizeQuietHours(userData?.quietHours));
  }, [userData?.quietHours]);

  const handleToggle = async (key) => {
    if (!currentUser?.uid) return;
    const next = !prefs[key];

    // Optimistic: the switch should move under the user's finger. Rolled back
    // below if the write fails, so the UI never claims a setting that isn't
    // stored — which for an opt-in notification would be a promise we break.
    setPrefs((p) => ({ ...p, [key]: next }));
    setSavingKey(key);

    try {
      // Dotted path so this merges. Assigning the whole map would drop every
      // key not rendered here.
      await updateDoc(doc(db, 'users', currentUser.uid), {
        [notificationPrefPath(key)]: next,
      });
    } catch (err) {
      setPrefs((p) => ({ ...p, [key]: !next }));
      toast.error('Could not save that preference');
    } finally {
      setSavingKey(null);
    }
  };

  const handleQuietChange = async (patch) => {
    if (!currentUser?.uid) return;
    const next = normalizeQuietHours({ ...quiet, ...patch });
    const previous = quiet;

    setQuiet(next);
    setSavingQuiet(true);
    try {
      await updateQuietHours(currentUser.uid, next);
    } catch (err) {
      setQuiet(previous);
      toast.error('Could not save quiet hours');
    } finally {
      setSavingQuiet(false);
    }
  };

  return (
    <div className="card p-6 mb-6">
      <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem] mb-4">
        <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
          <HiBell className="text-ink-500 text-sm" />
        </div>
        Notifications
      </h2>

      <div className="mb-6">
        <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-1">
          <HiMail className="text-sm" aria-hidden="true" />
          Email
        </p>
        {EMAIL_NOTIFICATION_PREFS.map((pref) => (
          <PrefRow
            key={pref.key}
            pref={pref}
            checked={prefs[pref.key]}
            saving={savingKey === pref.key}
            onToggle={handleToggle}
          />
        ))}
      </div>

      <div>
        <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-1">
          <HiDeviceMobile className="text-sm" aria-hidden="true" />
          On your phone
        </p>
        <p className="text-xs text-ink-500 mb-1">
          These control push notifications in the coparent mobile app. The web app
          doesn&rsquo;t send push notifications.
        </p>
        {PUSH_NOTIFICATION_PREFS.map((pref) => (
          <PrefRow
            key={pref.key}
            pref={pref}
            checked={prefs[pref.key]}
            saving={savingKey === pref.key}
            onToggle={handleToggle}
          />
        ))}
      </div>

      {/* Quiet hours */}
      <div className="mt-6 pt-5 border-t border-surface-100">
        <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-1">
          <HiOutlineMoon className="text-sm" aria-hidden="true" />
          Quiet hours
        </p>

        <div className="flex items-start justify-between gap-4 py-3">
          <div className="min-w-0">
            <label htmlFor="quiet-enabled" className="font-semibold text-ink-900 text-sm block">
              Pause notifications overnight
            </label>
            <p className="text-xs text-ink-500 mt-0.5">
              {quiet.enabled
                ? `No push notifications between ${formatQuietWindow(quiet)}.`
                : 'Silence push notifications during a window you choose.'}
            </p>
            {/* Says plainly what quiet hours do NOT do. A parent who assumes
                messages are held would be misled about their own thread. */}
            <p className="text-xs text-ink-400 mt-1.5">
              Messages still arrive on time &mdash; you just aren&rsquo;t buzzed for them. To
              deliver a message later, use &ldquo;Send later&rdquo; in a conversation.
            </p>
          </div>
          <Toggle
            id="quiet-enabled"
            checked={quiet.enabled}
            disabled={savingQuiet}
            onChange={(next) => handleQuietChange({ enabled: next })}
            label="Quiet hours"
          />
        </div>

        {quiet.enabled && (
          <div className="flex items-center gap-3 pb-1">
            <div className="flex-1">
              <label htmlFor="quiet-start" className="text-xs text-ink-500 font-medium block mb-1">
                From
              </label>
              <select
                id="quiet-start"
                value={quiet.startHour}
                disabled={savingQuiet}
                onChange={(e) => handleQuietChange({ startHour: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-ink-800 outline-none focus:ring-2 focus:ring-accent-200"
              >
                {HOUR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label htmlFor="quiet-end" className="text-xs text-ink-500 font-medium block mb-1">
                Until
              </label>
              <select
                id="quiet-end"
                value={quiet.endHour}
                disabled={savingQuiet}
                onChange={(e) => handleQuietChange({ endHour: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-ink-800 outline-none focus:ring-2 focus:ring-accent-200"
              >
                {HOUR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
